import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mountain, Loader2 } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const { settings } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Berhasil masuk");
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login gagal");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden md:flex flex-col justify-between p-12 text-white relative overflow-hidden" style={{ background: "var(--brand-primary)" }}>
        <div className="flex items-center gap-3">
          {settings?.logo_base64 ? (
            <img src={settings.logo_base64} alt="logo" className="w-12 h-12 rounded object-cover bg-white/10" />
          ) : (
            <div className="w-12 h-12 rounded bg-white/15 flex items-center justify-center">
              <Mountain className="w-7 h-7" />
            </div>
          )}
          <div className="font-display font-bold text-2xl">{settings?.business_name || "RentalPro"}</div>
        </div>
        <div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold leading-tight">
            Kelola persewaan outdoor <br /> dengan presisi profesional.
          </h1>
          <p className="mt-6 text-white/85 max-w-md text-base">
            POS cepat, kalender reservasi otomatis, pengingat WhatsApp H-1, printer termal Bluetooth, dan program referral, semua dalam satu dasbor.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-4 text-sm">
            <div className="border-l-2 border-white/40 pl-3"><div className="font-mono-tab font-bold text-2xl">58/80</div><div className="text-white/70 text-xs uppercase tracking-wider mt-1">mm Thermal</div></div>
            <div className="border-l-2 border-white/40 pl-3"><div className="font-mono-tab font-bold text-2xl">09:00</div><div className="text-white/70 text-xs uppercase tracking-wider mt-1">WITA H-1</div></div>
            <div className="border-l-2 border-white/40 pl-3"><div className="font-mono-tab font-bold text-2xl">24/7</div><div className="text-white/70 text-xs uppercase tracking-wider mt-1">Realtime Stok</div></div>
          </div>
        </div>
        <div className="text-xs text-white/60 uppercase tracking-widest">v1.0 · Progressive Web App</div>
      </div>

      {/* Login form */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="md:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded flex items-center justify-center" style={{ background: "var(--brand-primary)" }}>
              <Mountain className="w-6 h-6 text-white" />
            </div>
            <div className="font-display font-bold text-xl">{settings?.business_name || "RentalPro"}</div>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Masuk</div>
          <h2 className="font-display text-3xl font-bold text-neutral-900 mt-2">Selamat datang kembali</h2>
          <p className="text-neutral-500 mt-2 text-sm">Masuk untuk mengelola operasional persewaan Anda.</p>

          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email atau Username</Label>
              <Input id="email" data-testid="login-email" type="text" autoComplete="username" placeholder="email@contoh.com / username" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full h-11 text-white hover:opacity-90" style={{ background: "var(--brand-primary)" }}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Masuk"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
